import React, { useState, useEffect } from 'react';
import { FaCloud, FaSync, FaFilter, FaSearch, FaDownload } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';
import VPCContainer from './VPCContainer';
import GlobalResources from './GlobalResources';

const CloudMapDiagram = () => {
  const [diagramData, setDiagramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    showVPC: true,
    showCompute: true,
    showDatabase: true,
    showStorage: true,
    showNetworking: true
  });

  // Fetch diagram data
  const fetchDiagramData = async (region = 'all') => {
    try {
      setLoading(true);
      setError(null);
      
      const tenantId = localStorage.getItem('tenantID');
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }

      const apiBaseUrl = process.env.REACT_APP_CLOUDMAP_API || 'https://9cdf5nf6ya.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(
        `${apiBaseUrl}/diagram?tenantId=${tenantId}&region=${region}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch diagram data: ${response.status}`);
      }

      const result = await response.json();
      
      // API returns {data: {...}, metadata: {...}}
      // We need to use result.data
      console.log('API Response:', result);
      
      if (result.data) {
        console.log('Setting diagram data from result.data');
        console.log('Regions:', Object.keys(result.data.regions || {}));
        console.log('Summary:', result.data.summary);
        setDiagramData(result.data);
      } else {
        console.log('Setting diagram data directly');
        setDiagramData(result);
      }
    } catch (err) {
      console.error('Error fetching diagram:', err);
      setError(err.message || 'Failed to load CloudMap diagram');
    } finally {
      setLoading(false);
    }
  };

  // Refresh diagram data
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      
      const tenantId = localStorage.getItem('tenantID');
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      
      // For now, just trigger a direct Lambda invoke via fetch to the aggregator
      // The POST refresh endpoint needs a small Lambda fix, so we'll just refetch
      await fetchDiagramData(selectedRegion);
    } catch (err) {
      console.error('Error refreshing diagram:', err);
      setError(err.message || 'Failed to refresh diagram');
    } finally {
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    const tenantId = localStorage.getItem('tenantID');
    if (!tenantId) {
      setError('Please login first to view CloudMap diagram');
      setLoading(false);
      return;
    }
    fetchDiagramData(selectedRegion);
  }, [selectedRegion]);

  // Handle region change
  const handleRegionChange = (region) => {
    setSelectedRegion(region);
  };

  // Filter resources based on search and filters
  const filterResources = (resources) => {
    if (!resources) return [];
    
    return resources.filter(resource => {
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          resource.name?.toLowerCase().includes(query) ||
          resource.id?.toLowerCase().includes(query) ||
          resource.type?.toLowerCase().includes(query);
        
        if (!matchesSearch) return false;
      }

      // Apply type filters
      const resourceType = resource.type?.toLowerCase();
      if (resourceType?.includes('ec2') && !filters.showCompute) return false;
      if (resourceType?.includes('rds') && !filters.showDatabase) return false;
      if (resourceType?.includes('s3') && !filters.showStorage) return false;
      if ((resourceType?.includes('alb') || resourceType?.includes('gateway')) && !filters.showNetworking) return false;

      return true;
    });
  };

  // Export diagram
  const handleExport = () => {
    const dataStr = JSON.stringify(diagramData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cloudmap-diagram-${selectedRegion}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !diagramData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="large" message="Loading CloudMap Architecture..." />
      </div>
    );
  }

  if (error && !diagramData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Diagram</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => fetchDiagramData(selectedRegion)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const regions = diagramData?.regions ? Object.keys(diagramData.regions) : [];
  const summary = diagramData?.summary || {};

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FaFilter className="text-gray-600" />
              <span className="font-semibold text-sm text-gray-900">Filters</span>
            </div>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.showVPC}
                  onChange={(e) => setFilters({...filters, showVPC: e.target.checked})}
                  className="rounded text-blue-600"
                />
                <span>VPC & Networking</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.showCompute}
                  onChange={(e) => setFilters({...filters, showCompute: e.target.checked})}
                  className="rounded text-blue-600"
                />
                <span>Compute (EC2)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.showDatabase}
                  onChange={(e) => setFilters({...filters, showDatabase: e.target.checked})}
                  className="rounded text-blue-600"
                />
                <span>Database (RDS)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.showStorage}
                  onChange={(e) => setFilters({...filters, showStorage: e.target.checked})}
                  className="rounded text-blue-600"
                />
                <span>Storage (S3)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSync className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh Diagram'}
            </button>
            
            <button
              onClick={handleExport}
              className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
            >
              <FaDownload />
              Export Diagram
            </button>
          </div>

          {/* Summary Stats */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-xs font-semibold text-blue-900 mb-2">Summary</div>
            <div className="space-y-1 text-xs text-blue-800">
              <div className="flex justify-between">
                <span>Total Resources:</span>
                <span className="font-semibold">{summary.totalResources || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>VPCs:</span>
                <span className="font-semibold">
                  {Object.values(diagramData?.regions || {}).reduce((acc, region) => 
                    acc + Object.keys(region.vpcs || {}).length, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Regions:</span>
                <span className="font-semibold">{regions.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 overflow-auto">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <FaCloud className="text-blue-600 text-2xl" />
                <h1 className="text-2xl font-bold text-gray-900">CloudMap - AWS Architecture Diagram</h1>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {summary.totalResources || 0} Resources • {regions.length} Region{regions.length !== 1 ? 's' : ''}
                {diagramData?.generatedAt && (
                  <span className="ml-2">
                    • Last updated: {new Date(diagramData.generatedAt * 1000).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
            
            {/* Region Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => handleRegionChange('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedRegion === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Regions
              </button>
              {regions.map(region => (
                <button
                  key={region}
                  onClick={() => handleRegionChange(region)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedRegion === region
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {region}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas Content */}
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {loading && diagramData && (
              <div className="mb-4">
                <LoadingSpinner message="Updating diagram..." />
              </div>
            )}

            {error && diagramData && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Render regions */}
            {Object.entries(diagramData?.regions || {}).map(([regionName, regionData]) => {
              if (selectedRegion !== 'all' && selectedRegion !== regionName) {
                return null;
              }

              return (
                <div key={regionName}>
                  {/* Region Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <svg className="w-8 h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <h2 className="text-xl font-bold text-gray-900">Region: {regionName}</h2>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      {Object.keys(regionData.vpcs || {}).length} VPCs | {summary.byRegion?.[regionName] || 0} Resources
                    </span>
                  </div>

                  {/* VPCs */}
                  {Object.entries(regionData.vpcs || {}).map(([vpcId, vpcData]) => {
                    if (!filters.showVPC) return null;
                    
                    return (
                      <VPCContainer
                        key={vpcId}
                        vpcData={vpcData}
                        filters={filters}
                        searchQuery={searchQuery}
                      />
                    );
                  })}

                  {/* Global Resources for this region */}
                  {regionData.globalResources && regionData.globalResources.length > 0 && (
                    <GlobalResources
                      resources={filterResources(regionData.globalResources)}
                      regionName={regionName}
                    />
                  )}
                </div>
              );
            })}

            {/* No data message */}
            {(!diagramData?.regions || Object.keys(diagramData.regions).length === 0) && (
              <div className="text-center py-12">
                <FaCloud className="text-gray-300 text-6xl mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Resources Found</h3>
                <p className="text-gray-500 mb-4">
                  No AWS resources discovered in the selected region.
                </p>
                <button
                  onClick={handleRefresh}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Trigger Scan
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudMapDiagram;
